import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NavBar from "../NavBar";
import { adminToggleGroupSx } from "./adminStyles";
import {
  setAdminEnv,
  clearStagingSession,
} from "../../redux/slices/adminEnvSlice";
import StagingLoginDialog from "./StagingLoginDialog";

// Single entry point for the admin console. Renders the shared NavBar once plus
// a tab bar across the admin sections, and hosts the selected section through an
// <Outlet/> (the sections are nested routes under /admin in AppRoutes). Each
// section component now renders only its own content — the NavBar + surrounding
// chrome live here so there is one consistent admin shell instead of five
// standalone pages reached from five separate NavBar links.
const ADMIN_TABS = [
  { label: "Usage", path: "/admin/usage" },
  { label: "Events", path: "/admin/events" },
  { label: "Prescriptions", path: "/admin/prescriptions" },
  { label: "Feedback Triage", path: "/admin/feedback-triage" },
  { label: "Coach Chats", path: "/admin/coach-chats" },
  { label: "Coach Context", path: "/admin/coach-context" },
  { label: "Purge Workouts", path: "/admin/purge-workouts" },
  { label: "Reset Account", path: "/admin/reset-account" },
];

export default function AdminLayout() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  // Prod ⇄ Staging data-source switch (adminEnvSlice). Prod is byte-identical to
  // today; staging routes every admin fetch to the staging worker using a
  // separately-held staging admin token.
  const env = useSelector((s) => s.adminEnvSlice.env);
  const stagingToken = useSelector((s) => s.adminEnvSlice.stagingToken);
  const stagingEmail = useSelector((s) => s.adminEnvSlice.stagingEmail);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Safety net: if the console ever lands on staging with no held token (e.g. a
  // persisted staging session was dropped elsewhere), prompt the login rather
  // than sitting on blank, fetch-skipping dashboards. With the invariant enforced
  // in adminEnvSlice (staging env only after a successful login) this rarely
  // fires — the toggle no longer flips env to staging before a session exists.
  useEffect(() => {
    if (env === "staging" && !stagingToken) setDialogOpen(true);
  }, [env, stagingToken]);

  // Prefix match so a future nested detail route keeps its parent tab lit; falls
  // back to `false` (no selection) rather than letting MUI warn on an unknown
  // value — e.g. while /admin redirects to the default tab.
  const active =
    ADMIN_TABS.find((t) => pathname === t.path)?.path ??
    ADMIN_TABS.find((t) => pathname.startsWith(`${t.path}/`))?.path ??
    false;

  const handleEnvChange = (_e, next) => {
    if (!next || next === env) return;
    // Switching TO staging without a held token must NOT leave prod yet: just
    // open the login dialog and stay on prod (dashboards keep rendering prod
    // data behind the modal, and Production stays selectable). A successful
    // staging login commits the env via setStagingSession. Committing staging
    // here — before a session exists — is exactly what stranded the console when
    // the login failed or was abandoned. Switching to prod, or to staging with a
    // token already held, applies immediately.
    if (next === "staging" && !stagingToken) {
      setDialogOpen(true);
      return;
    }
    dispatch(setAdminEnv(next));
  };

  // Closing the dialog just dismisses it. Because the toggle no longer flips env
  // to staging before a session is held, the console is still on prod here —
  // there is nothing to revert. (setStagingSession is what moves us to staging,
  // and only on a successful login.)
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // clearStagingSession drops the token AND returns the env to prod atomically,
  // so this is a single dispatch — leaving env on staging with no token would be
  // the limbo state we've eliminated.
  const handleStagingLogout = () => {
    dispatch(clearStagingSession());
  };

  return (
    <Box>
      <NavBar />
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 3 },
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Tabs
            value={active}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Admin sections"
          >
            {ADMIN_TABS.map((t) => (
              <Tab
                key={t.path}
                label={t.label}
                value={t.path}
                component={Link}
                to={t.path}
              />
            ))}
          </Tabs>
          <ToggleButtonGroup
            value={env}
            exclusive
            size="small"
            onChange={handleEnvChange}
            aria-label="Admin data environment"
            sx={adminToggleGroupSx(theme)}
          >
            <ToggleButton value="prod" data-testid="admin-env-prod">
              Production
            </ToggleButton>
            <ToggleButton value="staging" data-testid="admin-env-staging">
              Staging
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {env === "staging" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              py: 1,
            }}
          >
            {stagingToken ? (
              <>
                <Typography
                  variant="caption"
                  data-testid="admin-staging-session"
                  sx={{ color: theme.palette.warning.main, fontWeight: 600 }}
                >
                  Staging: {stagingEmail}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={handleStagingLogout}
                >
                  Log out
                </Button>
              </>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDialogOpen(true)}
                data-testid="admin-staging-login-open"
              >
                Log in to staging
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Outlet />
      <StagingLoginDialog open={dialogOpen} onClose={handleDialogClose} />
    </Box>
  );
}
